import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useUpdateGameQuery } from "entities/game/queries/useUpdateGameQuery";
import { GameInformationTypeEnum, GameToUpdate } from "entities/game/types";
import { useServicePrimaryTagListQuery } from "entities/tagTypes/queries/useServicePrimaryTagListQuery";
import { useTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useThemeTagsListQuery } from "entities/themeTags/queries/useThemeTagListQuery";
import { useGamesPage } from "modules/games/context";
import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useShallow } from "zustand/react/shallow";
import { useUploadGameImages } from "../hooks/useUploadGameImages";
import { useGameTagsData } from "../stores/useGameTagsData";
import { useMetadata } from "../stores/useMetadata";
import {
  GameDetailsType,
  GameFile,
  GameMetadataTabsEnum,
  GameTabsEnum,
} from "./types";
import {
  extractServiceAndThemeTags,
  getGameInformation,
  getGameMetadataByType,
  serializeGameTagsToBlocks,
} from "./utils";

const [Provider, useGameDetails] = createContext<GameDetailsType>({
  name: "GameDetailsContext",
  hookName: "useGameDetails",
  providerName: "GameDetailsProvider",
});

const GameDetailsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { gameDetailsDrawer } = useGamesPage();
  const gameDetails = gameDetailsDrawer.drawerDetails;

  const { allowed: hasReadAccess } = useRolePermission("games:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "games:translations:read",
  );

  const [tab, setTab] = useState(GameTabsEnum.GENERAL);
  const [backgroundFile, setBackgroundFile] = useState<GameFile>(null);
  const [logoFile, setLogoFile] = useState<GameFile>(null);
  const [translations, setTranslations] = useState<Translations | undefined>(
    {},
  );

  const metadataStore = useMetadata();

  const { data: servicePrimaryTags } = useServicePrimaryTagListQuery();
  const { data: themeTags } = useThemeTagsListQuery();
  const { data: serviceTags } = useTagsListQuery();

  const { mutateAsync: updateGame, isPending: isSubmittingGame } =
    useUpdateGameQuery();

  const { gameTagTypeBlocks, setGameTagTypeBlocks, clearGameTagsData } =
    useGameTagsData(
      useShallow((state) => ({
        gameTagTypeBlocks: state.gameTagTypeBlocks,
        setGameTagTypeBlocks: state.setGameTagTypeBlocks,
        clearGameTagsData: state.clearGameTagsData,
      })),
    );

  useEffect(() => {
    if (gameDetails) {
      const gameTagBlocks = serializeGameTagsToBlocks({
        gameData: gameDetails,
        tags: serviceTags ?? [],
        themeTags: themeTags ?? [],
      });

      setGameTagTypeBlocks(gameTagBlocks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameDetails, serviceTags, themeTags]);

  useEffect(() => {
    if (gameDetails?.translations) {
      setTranslations(gameDetails.translations);
    }

    if (gameDetails?.metadata) {
      const metadata = gameDetails.metadata;

      metadataStore.setGameDescription(
        gameDetails.metadata.gameDescriptionFromProvider ?? "",
      );
      metadataStore.setGameOptimisedDescription(
        gameDetails.metadata.gameDescription ?? "",
      );
      metadataStore.setGameShortDescription(
        gameDetails.metadata.gameShortDescription ?? "",
      );
      metadataStore.setFeatures(gameDetails.metadata.features ?? []);
      metadataStore.setVolatility(
        (getGameMetadataByType(GameInformationTypeEnum.VOLATILITY, metadata)
          ?.value as string) ?? null,
      );
      metadataStore.setRtpExact(
        (getGameMetadataByType(GameInformationTypeEnum.RTP_EXACT, metadata)
          ?.value as string[]) ?? [],
      );
      metadataStore.setPaylines(
        String(
          getGameMetadataByType(GameInformationTypeEnum.PAYLINES, metadata)
            ?.value as string,
        ) ?? "",
      );

      metadataStore.setJackpotType(
        (getGameMetadataByType(GameInformationTypeEnum.JACKPOT_TYPE, metadata)
          ?.value as string[]) ?? [],
      );

      metadataStore.setLanguageSupport(
        (getGameMetadataByType(
          GameInformationTypeEnum.LANGUAGE_SUPPORT,
          metadata,
        )?.value as string[]) ?? [],
      );

      metadataStore.setReels(
        String(
          getGameMetadataByType(GameInformationTypeEnum.REELS, metadata)
            ?.value as string,
        ) ?? "",
      );

      metadataStore.setRows(
        String(
          getGameMetadataByType(GameInformationTypeEnum.ROWS, metadata)
            ?.value as string,
        ) ?? "",
      );

      metadataStore.setReleaseDate(
        String(
          getGameMetadataByType(GameInformationTypeEnum.RELEASE_DATE, metadata)
            ?.value as string,
        ) ?? "",
      );

      metadataStore.setAutoplay(
        (getGameMetadataByType(GameInformationTypeEnum.AUTOPLAY, metadata)
          ?.value as string) ?? null,
      );

      metadataStore.setMinStake(
        (getGameMetadataByType(GameInformationTypeEnum.MIN_STAKE, metadata)
          ?.value as string) ?? "",
      );

      metadataStore.setMaxStake(
        (getGameMetadataByType(GameInformationTypeEnum.MAX_STAKE, metadata)
          ?.value as string) ?? "",
      );

      metadataStore.setMaxWin(
        (getGameMetadataByType(GameInformationTypeEnum.MAX_WIN, metadata)
          ?.value as string) ?? "",
      );
    }
    setBackgroundFile(null);
    setLogoFile(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameDetails]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setTab(GameTabsEnum.TRANSLATIONS);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, gameDetails?.id]);

  const gameImagesLoadingStatus = useUploadGameImages({
    setBackgroundFile,
    setLogoFile,
    gameDetails: gameDetails || undefined,
  });

  const onCloseDrawer = () => {
    gameDetailsDrawer.closeDrawer();
    clearGameTagsData();
    setGameTagTypeBlocks([]);
    setTab(GameTabsEnum.GENERAL);
    metadataStore.setMetadataTab(GameMetadataTabsEnum.GENERAL);
  };

  const onSaveGameDetails = async () => {
    const { servicePrimaryTagIds, themePrimaryTagIds, serviceTags, themeTags } =
      extractServiceAndThemeTags(gameTagTypeBlocks);

    const gameDescription = metadataStore.gameDescription;
    const gameShortDescription = metadataStore.gameShortDescription;
    const gameOptimisedDescription =
      metadataStore.gameOptimisedDescription ?? "";
    const features = metadataStore.features.map((feature) => feature);

    const gameInformation = getGameInformation(metadataStore);

    const noMetadataToUpdate =
      !gameDescription &&
      !features.length &&
      !gameInformation &&
      !gameShortDescription;

    const metadataToUpdate = noMetadataToUpdate
      ? null
      : {
          ...gameDetails?.metadata,
          gameDescriptionFromProvider: gameDescription,
          gameDescription: gameOptimisedDescription,
          features,
          gameInformation,
          gameShortDescription: gameShortDescription,
        };

    if (!gameDetails) {
      console.warn("Game details are missing. Skipping...");
      return;
    }

    const game = {
      ...gameDetails,
      tagTypes: servicePrimaryTagIds,
      tags: serviceTags,
      themeTagTypes: themePrimaryTagIds,
      themeTags: themeTags,
      bgImage: backgroundFile,
      logoImage: logoFile,
      translations: translations || {},
      metadata: metadataToUpdate,
    } as GameToUpdate;

    await updateGame({ gameId: String(gameDetails.id), game });
    onCloseDrawer();
  };

  const onSaveGameTranslations = async () => {
    if (!gameDetails) {
      console.warn("Game details are missing. Skipping...");
      return;
    }

    const game = {
      translations: translations || {},
    } as GameToUpdate;

    await updateGame({ gameId: String(gameDetails.id), game });
    onCloseDrawer();
  };

  const value = {
    imageLoadingStatus: gameImagesLoadingStatus,
    tab,
    gameDetails,
    setTab,
    logoFile,
    setLogoFile,
    translations,
    setTranslations,
    backgroundFile,
    setBackgroundFile,
    onSaveGameDetails,
    onSaveGameTranslations,
    isSubmittingGame,
    servicePrimaryTags,
    onCloseDrawer,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { GameDetailsProvider, useGameDetails };
